import * as turf from '@turf/turf';

/**
 * Bán kính (m) fallback khi chưa có zoom — dùng cùng tỉ lệ với CLOSE_SNAP_RADIUS_PX.
 */
export const MAP_ANALYSIS_REGION_CLOSE_SNAP_METERS = 105;

/** Bán kính snap đóng vùng tính theo pixel (mặc định ~20px trên mọi mức zoom). */
export const MAP_ANALYSIS_REGION_CLOSE_SNAP_RADIUS_PX = 20;

/** Không thêm đỉnh nếu trùng gần đỉnh trước (tránh double-click). */
export const MAP_ANALYSIS_REGION_MIN_VERTEX_SPACING_M = 2.5;

/** Web Mercator meters/pixel at equator for 256px tiles. */
const METERS_PER_PIXEL_AT_EQUATOR_Z0 = 156543.03392;

/**
 * Tính toán bán kính snap (m) thích ứng theo mức zoom và vĩ độ để đạt được độ nhạy pixel nhất quán.
 * @param zoom Mức zoom hiện tại của bản đồ.
 * @param latitudeDeg Vĩ độ tại vị trí đang xét.
 * @param radiusPx Bán kính snap mong muốn tính bằng pixel (mặc định MAP_ANALYSIS_REGION_CLOSE_SNAP_RADIUS_PX).
 */
export function getAdaptiveSnapMeters(
  zoom: number,
  latitudeDeg: number,
  radiusPx: number = MAP_ANALYSIS_REGION_CLOSE_SNAP_RADIUS_PX,
): number {
  const latRad = (latitudeDeg * Math.PI) / 180;
  const metersPerPixel = (METERS_PER_PIXEL_AT_EQUATOR_Z0 * Math.cos(latRad)) / Math.pow(2, zoom);
  return metersPerPixel * radiusPx;
}

function distanceMeters(a: [number, number], b: [number, number]): number {
  return turf.distance(turf.point(a), turf.point(b), { units: 'meters' });
}

/**
 * Kiểm tra vòng [lng, lat][] (chưa khép) có tạo polygon hợp lệ, không tự cắt.
 */
export function isAnalysisPolygonRingValid(openRing: [number, number][]): boolean {
  if (openRing.length < 3) return false;
  const closed = [...openRing, openRing[0]];
  try {
    const poly = turf.polygon([closed]);
    if (!turf.booleanValid(poly)) return false;
    return turf.kinks(poly).features.length === 0;
  } catch {
    return false;
  }
}

/**
 * Có nên coi là nhấp để đóng vùng tại đỉnh đầu (đủ ≥3 đỉnh, trong bán kính snap).
 */
export function isWithinCloseSnapToFirst(
  openRing: [number, number][],
  coord: [number, number],
  snapMeters: number = MAP_ANALYSIS_REGION_CLOSE_SNAP_METERS,
): boolean {
  if (openRing.length < 3) return false;
  return distanceMeters(openRing[0], coord) <= snapMeters;
}

export function isTooCloseToLastVertex(
  openRing: [number, number][],
  coord: [number, number],
  minMeters: number = MAP_ANALYSIS_REGION_MIN_VERTEX_SPACING_M,
): boolean {
  if (openRing.length < 1) return false;
  return distanceMeters(openRing[openRing.length - 1], coord) < minMeters;
}

/**
 * Cạnh mới (đỉnh cuối → điểm mới) có cắt một cạnh nào của polyline đã có (không kể hai cạnh liền kề đỉnh cuối).
 */
export function wouldNewEdgeCrossOpenRing(
  openRing: [number, number][],
  newPoint: [number, number],
): boolean {
  const n = openRing.length;
  if (n < 2) return false;
  const last = openRing[n - 1];
  const newSeg = turf.lineString([last, newPoint]);
  const maxI = n - 3;
  for (let i = 0; i <= maxI; i++) {
    const edge = turf.lineString([openRing[i], openRing[i + 1]]);
    const x = turf.lineIntersect(newSeg, edge);
    if (x.features.length > 0) return true;
  }
  return false;
}
